import React from 'react';
import { 
  Box, 
  TableContainer, 
  TableContainerProps, 
  useTheme, 
  useMediaQuery 
} from '@mui/material';

interface ResponsiveTableProps extends TableContainerProps {
  children: React.ReactNode;
  /** Breakpoint where table switches to mobile scroll behavior */
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg';
  /** Enable enhanced mobile optimizations */
  mobileOptimized?: boolean;
}

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({ 
  children, 
  breakpoint = 'md',
  mobileOptimized = true,
  ...props 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(breakpoint));

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        ...(mobileOptimized && isMobile && {
          // Enhanced mobile optimizations
          '& .MuiTable-root': {
            minWidth: 'auto',
          },
          '& .MuiTableCell-root': {
            padding: '8px 4px',
            fontSize: '0.8rem',
            '&:first-of-type': {
              paddingLeft: '8px',
            },
            '&:last-of-type': {
              paddingRight: '8px',
            },
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            fontWeight: 600,
            fontSize: '0.75rem',
            padding: '12px 4px',
          },
          '& .MuiIconButton-root': {
            padding: '4px',
            '& .MuiSvgIcon-root': {
              fontSize: '1rem',
            },
          },
          '& .MuiChip-root': {
            height: '20px',
            fontSize: '0.7rem',
            '& .MuiChip-label': {
              padding: '0 6px',
            },
          },
        }),
        '& .MuiTableContainer-root': {
          maxWidth: '100%',
        },
        // Hide specific columns on mobile to reduce width
        ...(mobileOptimized && isMobile && {
          '& .mobile-hide': {
            display: 'none',
          },
          '& .mobile-priority-low': {
            display: 'none',
          },
        }),
        // Show all columns on larger screens  
        [theme.breakpoints.up(breakpoint)]: {
          '& .mobile-hide': {
            display: 'table-cell',
          },
          '& .mobile-priority-low': {
            display: 'table-cell',
          },
        },
      }}
    >
      <TableContainer {...props}>
        {children}
      </TableContainer>
    </Box>
  );
};

export default ResponsiveTable;